import getIdentifier from 'getIdentifier';

import { InsightsChartSpec } from 'services/streamInsightsQuery';

// Map the chat chartType to a FleetOps gadgetType. Note the naming difference:
// 'column' is a vertical bar chart (gadgetType 'bar'), while our 'bar'
// (Highcharts-style horizontal bars) maps to 'horizontalBar'. line/area both use
// the 'timeSeries' gadget (a trend), differentiated by the series type. The
// richer kinds map 1:1 onto their own gadget type.
const GADGET_TYPE: Record<InsightsChartSpec['chartType'], GadgetType> = {
  column: 'bar',
  bar: 'horizontalBar',
  pie: 'pie',
  line: 'timeSeries',
  area: 'timeSeries',
  donut: 'donut',
  radar: 'radar',
  waterfall: 'waterfall',
  packedBubble: 'packedBubble',
  funnel: 'funnel',
  pyramid: 'pyramid',
  scatter: 'scatter',
  bubble: 'bubble',
  heatmap: 'heatmap',
  sankey: 'sankey',
};

// 2b — when the spec is stacked, the series stack into one bar/area per category
// (a matrix or part-of-whole comparison). column/bar → stackedBar, line/area →
// stackedArea. Only the original cartesian kinds can stack (the backend's
// STACKABLE_CHART_TYPES never sets `stacked` on anything else), so this map is
// deliberately partial.
const STACKED_GADGET_TYPE: Partial<
  Record<InsightsChartSpec['chartType'], GadgetType>
> = {
  column: 'stackedBar',
  bar: 'stackedBar',
  line: 'stackedArea',
  area: 'stackedArea',
};

// How many series each gadget type can actually plot. The backend rejects an
// over-supplied spec before it reaches us, but a gadget written with too many
// series renders wrong rather than erroring, so truncate defensively here too.
// `max: 99` means "no meaningful cap".
const SERIES_RULE: Record<
  InsightsChartSpec['chartType'],
  { min: number; max: number }
> = {
  pie: { min: 1, max: 1 },
  donut: { min: 1, max: 1 },
  funnel: { min: 1, max: 1 },
  pyramid: { min: 1, max: 1 },
  waterfall: { min: 1, max: 1 },
  packedBubble: { min: 1, max: 1 },
  // X, Y and (bubble only) the metric sizing each point.
  scatter: { min: 2, max: 2 },
  bubble: { min: 2, max: 3 },
  // A cross-tab spends both dimensions, so there's no room for a second metric.
  heatmap: { min: 1, max: 1 },
  sankey: { min: 1, max: 1 },
  bar: { min: 1, max: 99 },
  column: { min: 1, max: 99 },
  line: { min: 1, max: 99 },
  area: { min: 1, max: 99 },
  radar: { min: 1, max: 99 },
};

// Gadget types whose data pipeline needs a group key. Without one useV5ChartData
// has nothing to group by and the gadget renders "No data". A trend satisfies
// these too — the group key falls back to `date`.
const NEEDS_GROUPING: ReadonlySet<InsightsChartSpec['chartType']> = new Set([
  'donut',
  'radar',
  'waterfall',
  'packedBubble',
  'funnel',
  'pyramid',
]);

// Gadget types that plot one point per DIMENSION MEMBER, joining their metrics
// on that member. A trend can't stand in for the dimension here, and emitting
// one alongside would change the query shape, so both are hard rules.
const NEEDS_DIMENSION_NOT_TREND: ReadonlySet<InsightsChartSpec['chartType']> =
  new Set(['scatter', 'bubble', 'heatmap', 'sankey']);

// Gadget types that cross-tabulate TWO categorical dimensions. buildSubGroupBy
// only emits the nested subGroupBy for these, and only when dimensionB is a
// text/boolean field with no trend alongside it.
const NEEDS_TWO_DIMENSIONS: ReadonlySet<InsightsChartSpec['chartType']> =
  new Set(['heatmap', 'sankey']);

// Fallback cap on each cross-tab axis when the spec didn't carry one. Mirrors
// the backend's MAX_CROSSTAB_DIM.
const DEFAULT_CROSSTAB_LIMIT = 20;

// Map the RESOLVED bins the server echoed (bucketResolved) onto the gadget
// dimension's rangeInput, so a saved histogram re-queries with the exact bins
// the user saw. `ranges` → custom cut-points; `interval` → an even-width
// histogram (the interval IS the resolved bin width, not the requested count).
const toRangeInput = (
  bucketResolved: NonNullable<InsightsChartSpec['source']['bucketResolved']>,
): CustomDimensionRangeInput | FixedDimensionRangeInput | undefined => {
  if (bucketResolved.ranges && bucketResolved.ranges.length > 0) {
    return {
      mode: 'custom',
      items: bucketResolved.ranges.map((r) => ({
        id: getIdentifier(),
        label: r.key,
        ...(r.from !== undefined ? { from: r.from } : {}),
        ...(r.to !== undefined ? { to: r.to } : {}),
      })),
    };
  }
  if (bucketResolved.interval !== undefined) {
    return { mode: 'fixed', fixed: bucketResolved.interval };
  }
  return undefined;
};

// Can this spec become a gadget that will actually render? The server-side
// buildChartSpec is the real gate, but V5GadgetForm's own validation is never
// consulted on the chat save path, so check the same invariants here before we
// write a dead gadget to Firestore.
export const canSaveSpec = (
  spec: InsightsChartSpec,
): { ok: true } | { ok: false; reason: string } => {
  const { chartType, source } = spec;
  const metricCount = 1 + (source.compareMetricIds?.length ?? 0);
  const rule = SERIES_RULE[chartType];

  // A computed series (a decompose_metric period-over-period delta per member)
  // has no live query behind it: the values were derived server-side from two
  // aggregations, and no gadget re-queries "the change vs the prior period".
  // Saving it would produce a gadget showing the metric itself, not the chart
  // the user saw.
  if (source.derived) {
    return {
      ok: false,
      reason:
        'This chart shows a change versus the prior period computed in the ' +
        'chat; a live gadget cannot reproduce it. Save the current-period ' +
        'breakdown instead.',
    };
  }

  // A distribution is a histogram — only column/bar, and only if the server
  // echoed the RESOLVED bins (without them the rebuilt gadget can't reproduce
  // the same buckets, so it would render an unbucketed total).
  if (source.bucketField) {
    if (chartType !== 'column' && chartType !== 'bar') {
      return {
        ok: false,
        reason: `A distribution over ${source.bucketField} must be a column or bar chart.`,
      };
    }
    if (!source.bucketResolved || !toRangeInput(source.bucketResolved)) {
      return {
        ok: false,
        reason: `The bin definition for ${source.bucketField} is missing, so the distribution can't be saved as a live gadget.`,
      };
    }
  }

  if (metricCount < rule.min) {
    return {
      ok: false,
      reason: `A ${chartType} chart needs at least ${rule.min} metrics.`,
    };
  }
  if (
    NEEDS_GROUPING.has(chartType) &&
    !source.breakdownField &&
    !source.trendByCalendarInterval
  ) {
    return {
      ok: false,
      reason: `A ${chartType} chart needs a breakdown to group by.`,
    };
  }
  if (NEEDS_DIMENSION_NOT_TREND.has(chartType) && !source.breakdownField) {
    return {
      ok: false,
      reason:
        `A ${chartType} chart plots one point per breakdown value, so it ` +
        'needs a breakdown (not a time trend).',
    };
  }
  if (NEEDS_TWO_DIMENSIONS.has(chartType)) {
    if (!source.breakdownField2 || !source.breakdownField2Type) {
      return {
        ok: false,
        reason: `A ${chartType} chart needs two breakdown dimensions.`,
      };
    }
    // buildSubGroupBy silently drops a sub-dimension that isn't categorical,
    // which would leave the gadget rendering "No data" with no explanation.
    if (
      source.breakdownField2Type !== 'text' &&
      source.breakdownField2Type !== 'boolean'
    ) {
      return {
        ok: false,
        reason:
          `A ${chartType} chart's second dimension must be a text or ` +
          `boolean field (got ${source.breakdownField2Type}).`,
      };
    }
  }
  return { ok: true };
};

// Rebuild a live V5ChartDefinition from a chat chart's source parameters. The
// resulting def re-queries fresh data when placed on a dashboard/report (it
// references the metric + dimension/trend, not the chat's snapshot values).
const buildChartDefFromSpec = (spec: InsightsChartSpec): V5ChartDefinition => {
  const { source } = spec;
  // Hard-gate the trend off for the kinds that plot per dimension member: a
  // trend changes the query shape (and, for a cross-tab, silently suppresses
  // the second dimension), leaving a gadget that renders permanently blank.
  const isTrend =
    !!source.trendByCalendarInterval &&
    !NEEDS_DIMENSION_NOT_TREND.has(spec.chartType);

  const gadgetType =
    (spec.stacked && STACKED_GADGET_TYPE[spec.chartType]) ||
    GADGET_TYPE[spec.chartType];

  // A single categorical dimension, no trend and no bins: the chat plotted one
  // member per category, so `categories` IS the member set it showed. A
  // cross-tab caps both of its axes further down instead.
  const isPureBreakdown =
    !!source.breakdownField &&
    !isTrend &&
    !source.bucketField &&
    !NEEDS_TWO_DIMENSIONS.has(spec.chartType);

  const groupByLimit =
    source.breakdownLimit ??
    (isPureBreakdown && spec.categories.length > 0
      ? spec.categories.length
      : undefined);

  const metrics = [
    { metricId: source.metricId },
    // Multi-metric comparison (2a): one extra series per compared metric.
    ...(source.compareMetricIds ?? []).map((compare) => ({
      metricId: compare.metricId,
    })),
  ].slice(0, SERIES_RULE[spec.chartType].max);

  return {
    id: getIdentifier(),
    name: spec.title || spec.valueLabel || 'Insights chart',
    gadgetType,
    series: metrics.map((metric, index) => ({
      id: `s${index}`,
      metricId: metric.metricId,
      ...(spec.chartType === 'area' ? { type: 'area' } : {}),
    })),
    // A numeric distribution: dimensionA carries the bucket field + rangeInput
    // (the resolved bins). getMetrics turns rangeInput into the gateway's
    // groupByRanges/groupByInterval and toBarSeries renders the bins ascending.
    // No grouping.defaultSort — bins are ordinal, not a metric-ordered top-N.
    ...(source.bucketField && source.bucketResolved
      ? {
          dimensionA: {
            field: source.bucketField,
            fieldType: 'float' as FleetOps.FieldType,
            isGroupByDayOfWeek: false,
            isGroupByMonth: false,
            ...(toRangeInput(source.bucketResolved)
              ? { rangeInput: toRangeInput(source.bucketResolved) }
              : {}),
          },
        }
      : {}),
    // 2b matrix: a breakdown AND a trend coexist (one series per breakdown value
    // over time). Emit both — the gadget pipeline pivots them. A pure trend or
    // pure breakdown emits only the one it has.
    ...(source.breakdownField
      ? {
          dimensionA: {
            field: source.breakdownField,
            fieldType: source.breakdownFieldType as FleetOps.FieldType,
            isGroupByDayOfWeek: false,
            isGroupByMonth: false,
          },
        }
      : {}),
    // Order the breakdown by the metric, matching what the chat asked the
    // gateway for. Without this buildGroupBy omits sortByMetric, so the
    // gateway's top-N picks a DIFFERENT set of members than the chat showed -
    // the explored chart then displays other states entirely (including
    // low-volume junk the metric-ordered top-N had excluded).
    // A ranked-list chart (find_outliers) carries its own direction — 'asc'
    // makes a "worst 5" save as a faithful bottom-5 — and the list's cap, so
    // the gadget shows the same N groups from the same end.
    ...(source.breakdownField
      ? {
          grouping: {
            defaultSort: {
              metricId: source.metricId,
              direction: source.sortDirection ?? 'desc',
            },
          },
          // Cap the rebuilt gadget at the number of members the chat actually
          // plotted. Without a limit buildGroupBy sends `limit: undefined` and
          // the live gadget queries EVERY member — the chat showed a
          // metric-ordered top-50 (or fewer once the chart guardrails folded
          // the tail), so the explored chart came back with hundreds of bars
          // the chat never drew (ENG-1023). A ranked list (find_outliers)
          // carries its own cap; anything else falls back to the plotted
          // category count. `groupByLimitMode` is set so the Configure rail's
          // limit toggle opens on the mode the def is actually in.
          //
          // Approximate in two known cases, both still far closer than
          // unbounded: a pie/donut that folded its tail into "Other" counts
          // that synthetic category, so the cap is one member generous; and
          // `excludedOutliers` (inline-only on the spec) can't be expressed as
          // a limit, so an excluded member can reappear here.
          ...(groupByLimit
            ? { groupByLimit, groupByLimitMode: 'top n' as const }
            : {}),
        }
      : {}),
    // Cross-tab: the second categorical dimension becomes dimensionB, which
    // buildSubGroupBy turns into the nested terms-in-terms query. groupByLimit
    // caps BOTH axes there, so carry the cap the chat applied.
    ...(NEEDS_TWO_DIMENSIONS.has(spec.chartType) && source.breakdownField2
      ? {
          dimensionB: {
            field: source.breakdownField2,
            fieldType: source.breakdownField2Type as FleetOps.FieldType,
            isGroupByDayOfWeek: false,
            isGroupByMonth: false,
          },
          groupByLimit: source.breakdownLimit ?? DEFAULT_CROSSTAB_LIMIT,
        }
      : {}),
    ...(isTrend
      ? {
          // An auto trend rebuilds with 'auto' so the gadget re-buckets by its
          // range like a native gadget; otherwise freeze the resolved bucket.
          trendByCalendarInterval: (source.trendIntervalAuto
            ? 'auto'
            : source.trendByCalendarInterval) as FleetOps.Interval,
        }
      : {}),
  };
};

export default buildChartDefFromSpec;
